import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bookmark, Building2, CalendarDays, Download, GalleryHorizontalEnd, Lock, Network, RotateCcw, Search, SlidersHorizontal, TrendingUp, X } from "lucide-react";
import "./researchSearch.css";
import "./mindshareHeaderControls.css";
import { MGIconButton } from "./editorial/MGEditorial";

export function MarketMindIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="9" cy="10" r="5" />
      <circle cx="17" cy="8" r="3" opacity="0.65" />
      <circle cx="16" cy="16" r="4" opacity="0.8" />
      <circle cx="7" cy="18" r="2.5" opacity="0.6" />
      <circle cx="4" cy="4" r="2" opacity="0.7" />
      <circle cx="20" cy="3" r="1.5" opacity="0.6" />
    </svg>
  );
}

const VIEWS = [
  { mode: "trended", label: "Cards", Icon: GalleryHorizontalEnd },
  { mode: "launches", label: "Launches", Icon: TrendingUp },
  { mode: "calendar", label: "Calendar", Icon: CalendarDays },
  { mode: "market", label: "Market Mind", Icon: MarketMindIcon },
  { mode: "publishers", label: "Publisher Atlas", Icon: Network },
  { mode: "business", label: "Business Charts", Icon: Building2 },
];

export function HeaderCenterSearch({ searchTerm, onSearchChange, ariaLabel, readOnly = false }) {
  return (
    <div className={`research-search ms-header-center-search${readOnly ? " is-locked" : ""}`}>
      <div className="research-search-pill" data-tooltip={readOnly ? "Available for paying members" : undefined}>
        <Search size={18} aria-hidden="true" />
        <input
          aria-label={ariaLabel}
          placeholder="Search games..."
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              onSearchChange("");
              event.currentTarget.blur();
            }
          }}
          disabled={readOnly}
        />
        {readOnly && <Lock size={15} aria-hidden="true" />}
        {!readOnly && searchTerm && (
          <button type="button" aria-label="Clear game search" title="Clear search" onClick={() => onSearchChange("")}>
            <X size={16} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function MindshareHeaderControls({ visible, viewMode, onViewChange, onOpenFilters, filtersOpen, filtersActive, toolsExpanded = false, searchTerm, onSearchChange, targetId = "mindshare-header-controls", savedControlsId = "mindshare-header-saved-controls", centerSearchTargetId = null, views = VIEWS, neutralViews = false, readOnly = false, ariaLabel = "mindSHARE views and filters", searchAriaLabel = "Search mindSHARE games" }) {
  const [target, setTarget] = useState(null);
  const [centerTarget, setCenterTarget] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [frontMode, setFrontMode] = useState(viewMode);
  const [viewsInteracting, setViewsInteracting] = useState(false);
  const toolbarRef = useRef(null);
  useEffect(() => {
    if (!searchOpen) return undefined;
    const closeSearch = (event) => {
      if (!toolbarRef.current?.contains(event.target)) setSearchOpen(false);
    };
    document.addEventListener("click", closeSearch);
    return () => document.removeEventListener("click", closeSearch);
  }, [searchOpen]);
  useEffect(() => {
    if (!viewsInteracting) setFrontMode(viewMode);
  }, [viewMode, viewsInteracting]);
  useEffect(() => {
    const findTarget = () => {
      setTarget(document.getElementById(targetId));
      setCenterTarget(centerSearchTargetId ? document.getElementById(centerSearchTargetId) : null);
    };
    findTarget();
    const observer = new MutationObserver(findTarget);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [targetId, centerSearchTargetId]);
  if (!visible || !target) return null;
  const currentView = views.find(({ mode }) => mode === frontMode) || views[0];
  const CurrentViewIcon = currentView.Icon;
  const centered = Boolean(centerSearchTargetId);
  const lockedTooltip = "Available for paying members";
  const lockProps = readOnly
    ? { "aria-disabled": "true", "data-tooltip": lockedTooltip, onClick: (event) => event.preventDefault() }
    : {};

  return <>{centered && centerTarget && createPortal(
    <HeaderCenterSearch searchTerm={searchTerm} onSearchChange={onSearchChange} ariaLabel={searchAriaLabel} readOnly={readOnly} />,
    centerTarget
  )}{createPortal(
    <div ref={toolbarRef} className="ms-header-tools" role="toolbar" aria-label={ariaLabel}
      onMouseDownCapture={(event) => {
        // Keep inline stacks stationary until a button's click has completed.
        if (event.target.closest("button")) event.preventDefault();
      }}
      onClick={(event) => {
        if (!event.target.closest(".ms-header-search")) setSearchOpen(false);
      }}
    >
      <div className={`ms-header-tools-stack${searchOpen ? " is-search-open" : ""}${centered ? " has-center-search" : ""}${toolsExpanded ? " is-expanded" : ""}${readOnly ? " is-readonly" : ""}`}>
          <MGIconButton className={`ms-header-icon ms-header-stack-trigger${filtersOpen || filtersActive ? " is-active" : ""}${filtersActive ? " has-filters" : ""}`} onClick={readOnly ? (event) => event.preventDefault() : onOpenFilters} aria-label="Open filters" aria-expanded={filtersOpen} aria-disabled={readOnly || undefined} data-filters-active={Boolean(filtersActive)} data-tooltip={readOnly ? lockedTooltip : filtersOpen ? undefined : filtersActive ? "Filters applied" : "Filters"}>
            <SlidersHorizontal size={16} aria-hidden="true" />
          </MGIconButton>
        <div className="ms-header-tools-stack-items" aria-label="Tools">
          {readOnly ? (
            <>
              {[RotateCcw, Download, Bookmark].map((Icon, index) => (
                <MGIconButton key={index} className="ms-header-icon is-locked" aria-disabled="true" data-tooltip={lockedTooltip} onClick={(event) => event.preventDefault()} aria-label={lockedTooltip}>
                  <Icon size={16} aria-hidden="true" />
                </MGIconButton>
              ))}
            </>
          ) : (
          <div id={savedControlsId} className="ms-header-saved-controls" />
          )}
          {!centered && <div className="ms-header-search">
            <MGIconButton className={`ms-header-icon${searchOpen ? " is-active" : ""}${readOnly ? " is-locked" : ""}`} onClick={readOnly ? (event) => event.preventDefault() : () => setSearchOpen((open) => !open)} aria-label="Search games" aria-expanded={searchOpen} aria-disabled={readOnly || undefined} data-tooltip={readOnly ? lockedTooltip : "Search games"}>
              <Search size={16} aria-hidden="true" />
            </MGIconButton>
            {searchOpen && (
              <input
                autoFocus
                aria-label={searchAriaLabel}
                placeholder="Search games..."
                value={searchTerm}
                onChange={(event) => onSearchChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    onSearchChange("");
                    setSearchOpen(false);
                  }
                }}
              />
            )}
          </div>}
        </div>
      </div>
      <span className="ms-header-divider" aria-hidden="true" />
      <div className={`ms-header-views-stack${readOnly ? " is-readonly" : ""}`}
        onMouseEnter={() => setViewsInteracting(true)}
        onMouseLeave={(event) => {
          setViewsInteracting(false);
          if (event.currentTarget.contains(document.activeElement)) document.activeElement.blur();
        }}
        onFocusCapture={() => setViewsInteracting(true)}
        onBlurCapture={(event) => {
          if (event.currentTarget.matches(":hover")) return;
          if (!event.currentTarget.contains(event.relatedTarget)) setViewsInteracting(false);
        }}
      >
        <MGIconButton className={`ms-header-icon ms-header-stack-trigger${!neutralViews && viewMode === currentView.mode ? " is-active" : ""}`} onClick={() => onViewChange(currentView.mode)} aria-label={currentView.label} aria-pressed={viewMode === currentView.mode} data-tooltip={currentView.label}>
          <CurrentViewIcon size={17} aria-hidden="true" />
        </MGIconButton>
        <div className="ms-header-views-stack-items" aria-label="Views">
      {views.filter(({ mode }) => mode !== currentView.mode).map(({ mode, label, Icon }) => (
        <MGIconButton key={mode} className={`ms-header-icon${!neutralViews && viewMode === mode ? " is-active" : ""}${readOnly ? " is-locked" : ""}`} onClick={readOnly ? (event) => event.preventDefault() : () => onViewChange(mode)} aria-label={label} aria-pressed={viewMode === mode} aria-disabled={readOnly || undefined} data-tooltip={readOnly ? `${label} — ${lockedTooltip}` : label}>
          <Icon size={17} aria-hidden="true" />
        </MGIconButton>
      ))}
        </div>
      </div>
    </div>,
    target
  )}</>;
}